// Win Your Wedding — staff page: leads list, couple record, add couple,
// Final Night door check-in and the live draw.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Copy, Check, Maximize2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNotify } from '../components/NotifyProvider';
import {
  fmtTime, fmtSlot, fmtAgo, daysAgo, coupleStatus, STATUS_LABEL, errorText,
  bookLink, tonightLink, enterLink, WYW_GREEN, WYW_GOLD,
} from '../lib/wywService';

const SOURCE_LABEL = { meta: 'Meta', website: 'Website', qr: 'QR code', email: 'Email', manual: 'Added by staff', walk_in: 'Walk-in' };
const SOURCE_ORDER = ['meta', 'website', 'qr', 'email', 'manual', 'walk_in'];

const STATUS_STYLE = {
  none: { background: '#FFF4E5', color: '#8A4B00' },
  booked: { background: '#F1EEE8', color: '#2B2B2B' },
  entered: { background: '#E5F4E4', color: '#1E5A2B' },
};

function Chip({ on, children, onClick, warm }) {
  const style = on
    ? { background: WYW_GREEN, color: '#fff', borderColor: WYW_GREEN }
    : warm ? { background: '#FFF4E5', color: '#8A4B00', borderColor: '#E2B26B' } : { background: '#fff', color: '#2B2B2B', borderColor: '#D6D2C8' };
  return <button onClick={onClick} className="h-10 px-4 rounded-full border text-[15px] font-bold" style={style}>{children}</button>;
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} my-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#ECE8E0]">
          <h2 className="text-xl font-bold" style={{ color: WYW_GREEN }}>{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function CopyButton({ text, label }) {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={() => navigator.clipboard.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1500); })}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#D6D2C8] bg-white text-sm font-bold">
      {ok ? <Check size={15} /> : <Copy size={15} />} {label}
    </button>
  );
}

// ── Slot picker used by staff ──
function SlotPicker({ couple, evenings, onBooked, onClose }) {
  const notify = useNotify();
  const [day, setDay] = useState(couple.evening_id || couple.preferred_evening || evenings[0]?.id);
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!day) return;
    supabase.rpc('wyw_slots', { p_evening: day }).then(({ data }) => setSlots(data || []));
  }, [day]);

  const pick = async (slotAt) => {
    setBusy(true);
    const { error } = await supabase.rpc('wyw_book', { p_couple: couple.id, p_slot: slotAt, p_host: null });
    setBusy(false);
    if (error) { notify.notifyError(errorText(error)); return; }
    notify.notifySuccess(`Booked ${fmtSlot(slotAt)}. Confirmation email goes within 5 minutes.`);
    onBooked();
  };

  return (
    <Modal title={`Show-around for ${couple.names}`} onClose={onClose}>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {evenings.map((e) => (
          <button key={e.id} onClick={() => setDay(e.id)} className="h-12 rounded-xl border font-bold"
            style={e.id === day ? { background: WYW_GREEN, color: '#fff', borderColor: WYW_GREEN } : { background: '#fff', borderColor: '#D6D2C8', color: WYW_GREEN }}>{e.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {slots.map((s) => {
          const free = s.primary_free + s.backup_free;
          const mine = couple.slot_at && new Date(couple.slot_at).getTime() === new Date(s.slot_at).getTime();
          return (
            <button key={s.slot_at} disabled={busy || (free === 0 && !mine)} onClick={() => pick(s.slot_at)}
              className="h-14 rounded-lg border text-sm font-bold flex flex-col items-center justify-center disabled:opacity-40"
              style={{ borderColor: mine ? WYW_GREEN : '#D6D2C8', background: mine ? '#E5F4E4' : '#fff' }}>
              {fmtTime(s.slot_at)}
              <span className="text-xs font-normal text-[#5E5E5E]">{mine ? 'current' : free === 0 ? 'full' : s.primary_free ? `${s.primary_free} free` : 'backup only'}</span>
            </button>
          );
        })}
      </div>
      <p className="text-sm text-[#5E5E5E] mt-4">The host is set automatically: Niall and Jacqueline first, then Ronan and Simone.</p>
    </Modal>
  );
}

// ── Add couple ──
function AddCouple({ onClose, onSaved }) {
  const notify = useNotify();
  const [f, setF] = useState({ names: '', email: '', phone: '', guests: '', wedding_year: '', source: 'manual', done: false, host_id: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const save = async () => {
    if (!f.names.trim()) { notify.notifyError('Add their names.'); return; }
    const row = { names: f.names.trim(), email: f.email.trim().toLowerCase() || null, phone: f.phone.trim() || null,
      guests: f.guests || null, wedding_year: f.wedding_year || null, source: f.done ? 'walk_in' : f.source };
    if (f.done) Object.assign(row, { entered_at: new Date().toISOString(), entered_by: 'Staff', host_id: f.host_id || null });
    const { error } = await supabase.from('wyw_couples').insert(row);
    if (error) { notify.notifyError(/duplicate/.test(error.message) ? 'That email is already entered.' : error.message); return; }
    notify.notifySuccess(f.done ? 'Added and fully entered. Their email and ticket go within 5 minutes.' : 'Added. Their "book your show-around" email goes within 5 minutes.');
    onSaved();
  };
  const input = 'w-full h-11 px-3 rounded-lg border border-[#D6D2C8] text-base';
  return (
    <Modal title="Add couple" onClose={onClose}>
      <div className="space-y-3">
        <input className={input} placeholder="Names, e.g. Aoife & Conor" value={f.names} onChange={set('names')} />
        <input className={input} placeholder="Email" type="email" value={f.email} onChange={set('email')} />
        <input className={input} placeholder="Mobile" value={f.phone} onChange={set('phone')} />
        <div className="grid grid-cols-2 gap-3">
          <select className={input} value={f.wedding_year} onChange={set('wedding_year')}>
            <option value="">Wedding year…</option><option>2027</option><option>2028</option><option>Not sure yet</option>
          </select>
          <select className={input} value={f.guests} onChange={set('guests')}>
            <option value="">Guests…</option><option>Under 75</option><option>75–120</option><option>120–200</option><option>200+</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-base"><input type="checkbox" checked={f.done} onChange={set('done')} className="w-5 h-5" /> Walk-in: they've had the show-around now</label>
        {f.done && (
          <select className={input} value={f.host_id} onChange={set('host_id')}>
            <option value="">Who showed them around?</option>
            <option value="niall">Niall</option><option value="jacqueline">Jacqueline</option><option value="ronan">Ronan</option><option value="simone">Simone</option>
          </select>
        )}
        <button onClick={save} className="w-full h-12 rounded-xl text-white font-bold text-base" style={{ background: WYW_GREEN }}>Save</button>
      </div>
    </Modal>
  );
}

// ── Couple record ──
function CoupleRecord({ couple, hosts, onClose, onChanged, onBook }) {
  const notify = useNotify();
  const [log, setLog] = useState([]);
  const [notes, setNotes] = useState(couple.notes || '');
  useEffect(() => {
    supabase.from('wyw_email_log').select('kind,status,sent_at').eq('couple_id', couple.id).order('sent_at').then(({ data }) => setLog(data || []));
  }, [couple.id]);
  const host = hosts.find((h) => h.id === couple.host_id);
  const st = coupleStatus(couple);
  const update = async (patch, msg) => {
    const { error } = await supabase.from('wyw_couples').update(patch).eq('id', couple.id);
    if (error) notify.notifyError(error.message); else { if (msg) notify.notifySuccess(msg); onChanged(); }
  };
  const KIND = { welcome: "You're in: book a slot", booked: 'Booked confirmation', reminder: 'Day-before reminder', entered: 'Fully entered + ticket', final_reminder: 'Final Night reminder' };
  const btn = 'h-11 px-4 rounded-lg font-bold text-base';
  return (
    <Modal title={couple.names} onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {couple.phone && <a href={`tel:${couple.phone.replace(/\s+/g, '')}`} className={`${btn} text-white inline-flex items-center`} style={{ background: WYW_GREEN }}>Call {couple.phone}</a>}
            {couple.email && <a href={`mailto:${couple.email}`} className={`${btn} border inline-flex items-center`} style={{ borderColor: WYW_GREEN, color: WYW_GREEN }}>Email</a>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-base">
            <div><div className="text-sm text-[#5E5E5E]">Wedding year</div>{couple.wedding_year || '—'}</div>
            <div><div className="text-sm text-[#5E5E5E]">Guests</div>{couple.guests || '—'}</div>
            <div><div className="text-sm text-[#5E5E5E]">Venue</div>{couple.venue_status || '—'}</div>
            <div><div className="text-sm text-[#5E5E5E]">Enquired</div>{fmtAgo(couple.enquired_at)}, from {SOURCE_LABEL[couple.source] || couple.source}</div>
            <div><div className="text-sm text-[#5E5E5E]">Entry number</div>#{String(couple.entry_no).padStart(3, '0')}</div>
            <div className="break-all"><div className="text-sm text-[#5E5E5E]">Email</div>{couple.email || '—'}</div>
          </div>
          {couple.message && <div className="text-sm bg-[#F5F4F2] rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-y-auto">{couple.message}</div>}
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (couple.notes || '') && update({ notes })}
            placeholder="Notes" rows={3} className="w-full rounded-lg border border-[#D6D2C8] p-3 text-base" />
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-[#ECE8E0] p-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold tracking-wide text-[#5E5E5E]">SHOW-AROUND</div>
              <div className="text-lg font-bold" style={{ color: WYW_GREEN }}>{couple.slot_at ? `${fmtSlot(couple.slot_at)} with ${host?.name || '—'}` : 'Not booked yet'}</div>
            </div>
            <button onClick={onBook} className={`${btn} border shrink-0`} style={{ borderColor: WYW_GREEN, color: WYW_GREEN }}>{couple.slot_at ? 'Change' : 'Book a slot'}</button>
          </div>
          <div className="rounded-xl p-4" style={STATUS_STYLE[st]}>
            <div className="text-sm font-bold tracking-wide">STATUS</div>
            <div className="text-lg font-bold">{st === 'entered' ? 'Fully entered · in the draw' : st === 'booked' ? 'Booked · not in the draw yet' : 'Not booked yet'}</div>
            {couple.checked_in_at && <div className="text-base font-bold mt-1">✓ Checked in on the Final Night</div>}
            <div className="flex flex-wrap gap-2 mt-3">
              {!couple.entered_at
                ? <button onClick={() => update({ entered_at: new Date().toISOString(), entered_by: 'Staff' }, 'Marked done. Their email and ticket go within 5 minutes.')} className={`${btn} text-white`} style={{ background: WYW_GREEN }}>Mark show-around done</button>
                : !couple.entered_sent_at && <button onClick={() => update({ entered_at: null, entered_by: null }, 'Undone.')} className={`${btn} bg-white border border-[#9FB3A8]`}>Undo done</button>}
              {couple.entered_at && !couple.checked_in_at && <button onClick={() => update({ checked_in_at: new Date().toISOString() }, 'Checked in.')} className={`${btn} bg-white border`}>Check in (Final Night)</button>}
            </div>
          </div>
          <div className="rounded-xl border border-[#ECE8E0] p-4 text-base">
            <div className="text-sm font-bold tracking-wide text-[#5E5E5E] mb-2">EMAILS (SENT FOR YOU)</div>
            {log.length === 0 && <div className="text-[#5E5E5E]">None yet</div>}
            {log.map((l, i) => <div key={i}>{l.status === 'sent' ? '✓' : '✗ failed:'} {KIND[l.kind] || l.kind} · {fmtAgo(l.sent_at)}</div>)}
            {host && <div className="text-sm text-[#5E5E5E] mt-2">If they reply, it goes to {host.name}'s own email.</div>}
            <div className="mt-3"><CopyButton text={bookLink(couple.token)} label="Copy their booking link" /></div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Draw night ──
function DrawNight({ couples, settings, draw, reload }) {
  const notify = useNotify();
  const [q, setQ] = useState('');
  const [big, setBig] = useState(false);
  const entered = couples.filter((c) => c.entered_at);
  const here = entered.filter((c) => c.checked_in_at);
  const locked = !!settings?.pool_locked_at;
  const pool = locked ? here.filter((c) => new Date(c.checked_in_at) <= new Date(settings.pool_locked_at)) : here;
  const byId = Object.fromEntries(couples.map((c) => [c.id, c]));
  const drawn = [...draw].sort((a, b) => a.position - b.position);
  const winner = drawn.find((d) => d.result === 'winner');
  const runners = drawn.filter((d) => d.result === 'runner_up').length;
  const last = drawn[drawn.length - 1];

  const lock = async () => {
    if (!window.confirm(`Close check-in and lock the draw with ${here.length} couples?`)) return;
    await supabase.from('wyw_settings').update({ pool_locked_at: new Date().toISOString() }).eq('id', 1);
    reload();
  };
  const drawNext = async () => {
    const { error } = await supabase.rpc('wyw_draw_next');
    if (error) notify.notifyError(errorText(error));
    reload();
  };
  const mark = async (d, result) => {
    if (result === 'runner_up' && d.result !== 'runner_up' && runners >= 5) { notify.notifyError('5 runner-ups already marked.'); return; }
    if (result === 'winner' && winner && winner.id !== d.id) await supabase.from('wyw_draw').update({ result: null }).eq('id', winner.id);
    await supabase.from('wyw_draw').update({ result: d.result === result ? null : result }).eq('id', d.id);
    reload();
  };
  const checkIn = async (c) => {
    await supabase.from('wyw_couples').update({ checked_in_at: c.checked_in_at ? null : new Date().toISOString() }).eq('id', c.id);
    reload();
  };

  let stage = locked ? 'POOL LOCKED' : 'CHECK-IN OPEN', name = `${here.length} couples here`, sub = locked ? 'Ready to draw 10 finalists' : 'Check-in closes at 8:45pm';
  if (locked && !drawn.length) name = `${pool.length} couples in the draw`;
  if (last) { stage = `FINALIST ${last.position} OF 10`; name = byId[last.couple_id]?.names; sub = last.position < 10 ? 'Come up to the stage!' : 'All 10 finalists drawn · on to the games'; }
  if (winner) { stage = 'OUR WINNERS'; name = byId[winner.couple_id]?.names; sub = 'Your wedding, on us'; }

  const stageBox = (
    <div className={`rounded-2xl flex flex-col items-center justify-center gap-4 p-6 text-center ${big ? 'fixed inset-0 z-50 rounded-none' : 'min-h-[360px]'}`} style={{ background: WYW_GREEN }}>
      <div className="text-sm font-bold tracking-[0.24em]" style={{ color: WYW_GOLD }}>{stage}</div>
      <div className={`${big ? 'text-7xl' : 'text-5xl'} text-white`} style={{ fontFamily: 'Georgia, serif' }}>{name}</div>
      <div className="text-white/80 text-lg">{sub}</div>
      {!locked && <button onClick={lock} className="h-14 px-8 rounded-xl text-lg font-bold" style={{ background: WYW_GOLD, color: WYW_GREEN }}>Close check-in and lock the pool</button>}
      {locked && drawn.length < 10 && <button onClick={drawNext} className="h-16 px-12 rounded-xl text-xl font-bold" style={{ background: WYW_GOLD, color: WYW_GREEN }}>{drawn.length ? `Draw finalist ${drawn.length + 1}` : 'Draw first finalist'}</button>}
      <button onClick={() => setBig(!big)} className="text-white/70 text-sm inline-flex items-center gap-1"><Maximize2 size={14} /> {big ? 'Exit full screen' : 'Full screen for the projector'}</button>
    </div>
  );

  const list = q.trim() ? entered.filter((c) => c.names.toLowerCase().includes(q.toLowerCase()) || String(c.entry_no) === q.replace(/\D/g, '')) : [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['Fully entered', entered.length], ['Checked in tonight', here.length], ['Draw pool', locked ? `${pool.length} · locked` : 'Open'], ['Not here', entered.length - here.length]].map(([l, v]) => (
          <div key={l} className="bg-white rounded-xl p-4"><div className="text-sm text-[#5E5E5E]">{l}</div><div className="text-2xl font-bold">{v}</div></div>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-5">
        {stageBox}
        <div className="bg-white rounded-2xl p-4 space-y-2">
          <div className="text-sm font-bold tracking-wide text-[#5E5E5E]">FINALISTS · AFTER THE GAMES, MARK THE RESULT</div>
          {drawn.length === 0 && <div className="text-[#5E5E5E]">Nobody drawn yet.</div>}
          {drawn.map((d) => (
            <div key={d.id} className="flex items-center gap-2 h-12 px-2 rounded-lg" style={{ background: d.result === 'winner' ? '#FFF4E5' : d.result === 'runner_up' ? '#E5F4E4' : '#F5F4F2' }}>
              <span className="w-6 text-sm font-bold text-[#5E5E5E]">{d.position}</span>
              <span className="flex-1 font-bold truncate">{byId[d.couple_id]?.names}</span>
              <button onClick={() => mark(d, 'winner')} className="h-8 px-2 rounded-md border text-xs font-bold" style={d.result === 'winner' ? { background: WYW_GOLD, borderColor: WYW_GOLD } : { borderColor: '#D6D2C8' }}>Winner</button>
              <button onClick={() => mark(d, 'runner_up')} className="h-8 px-2 rounded-md border text-xs font-bold" style={d.result === 'runner_up' ? { background: WYW_GREEN, color: '#fff', borderColor: WYW_GREEN } : { borderColor: '#D6D2C8' }}>Runner-up</button>
            </div>
          ))}
          {drawn.length === 10 && <div className="text-sm font-bold pt-1" style={{ color: winner && runners === 5 ? '#1E5A2B' : '#5E5E5E' }}>{winner && runners === 5 ? '✓ Result saved.' : `Mark 1 winner and 5 runner-ups (${runners} of 5 marked).`}</div>}
        </div>
      </div>
      <div className="bg-white rounded-2xl p-4">
        <div className="text-sm font-bold tracking-wide text-[#5E5E5E] mb-2">DOOR CHECK-IN · scan their ticket QR, or search here</div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or entry number" className="w-full h-12 px-3 rounded-lg border border-[#D6D2C8] text-base" />
        <div className="divide-y divide-[#F1EEE8] mt-2">
          {list.slice(0, 12).map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2">
              <span className="text-base"><b>{c.names}</b> <span className="text-[#5E5E5E]">#{String(c.entry_no).padStart(3, '0')}</span></span>
              <button onClick={() => checkIn(c)} className="h-10 px-4 rounded-lg font-bold" style={c.checked_in_at ? { background: '#E5F4E4', color: '#1E5A2B' } : { background: WYW_GREEN, color: '#fff' }}>{c.checked_in_at ? '✓ Checked in' : 'Check in'}</button>
            </div>
          ))}
          {q.trim() && !list.length && <div className="py-2 text-[#8A4B00] font-bold">Not found among fully entered couples. They're not in the draw.</div>}
        </div>
      </div>
    </div>
  );
}

// ── Ticket scanned at the door: ?wyw_ticket=TOKEN ──
function TicketCheckIn({ token, onDone }) {
  const [c, setC] = useState(undefined);
  const load = useCallback(() => supabase.from('wyw_couples').select('*').eq('token', token).maybeSingle().then(({ data }) => setC(data || null)), [token]);
  useEffect(() => { load(); }, [load]);
  const close = () => { const u = new URL(window.location.href); u.searchParams.delete('wyw_ticket'); window.history.replaceState({}, '', u); onDone(); };
  if (c === undefined) return null;
  const ok = c && c.entered_at;
  return (
    <Modal title="Final Night ticket" onClose={close}>
      {!c && <p className="text-lg font-bold text-red-700">This ticket isn't recognised.</p>}
      {c && (
        <div className="text-center space-y-3">
          <div className="text-2xl font-bold">{c.names}</div>
          <div className="text-[#5E5E5E]">Entry #{String(c.entry_no).padStart(3, '0')}</div>
          {!ok && <p className="text-lg font-bold text-[#8A4B00]">Not fully entered: they didn't complete a show-around, so they're not in the draw.</p>}
          {ok && c.checked_in_at && <p className="text-lg font-bold text-[#1E5A2B]">✓ Already checked in</p>}
          {ok && !c.checked_in_at && (
            <button onClick={async () => { await supabase.from('wyw_couples').update({ checked_in_at: new Date().toISOString() }).eq('id', c.id); load(); }}
              className="w-full h-16 rounded-xl text-white text-xl font-bold" style={{ background: WYW_GREEN }}>Check in</button>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Page ──
export default function WinYourWedding() {
  const [tab, setTab] = useState('leads');
  const [couples, setCouples] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [evenings, setEvenings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [draw, setDraw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState('All');
  const [range, setRange] = useState('Any time');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [bookId, setBookId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [ticket, setTicket] = useState(() => { try { return new URLSearchParams(window.location.search).get('wyw_ticket'); } catch (_) { return null; } });
  const notify = useNotify();

  const load = useCallback(async () => {
    const [c, h, e, s, d] = await Promise.all([
      supabase.from('wyw_couples').select('*').order('enquired_at', { ascending: false }),
      supabase.from('wyw_hosts').select('*').order('sort'),
      supabase.from('wyw_evenings').select('*').order('starts_at'),
      supabase.from('wyw_settings').select('*').eq('id', 1).maybeSingle(),
      supabase.from('wyw_draw').select('*'),
    ]);
    if (c.error) notify.notifyError(`Couldn't load the competition: ${c.error.message}`);
    setCouples(c.data || []); setHosts(h.data || []); setEvenings(e.data || []); setSettings(s.data); setDraw(d.data || []);
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const ch = supabase.channel('wyw').on('postgres_changes', { event: '*', schema: 'public', table: 'wyw_couples' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'wyw_draw' }, () => load()).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [load]);

  const rows = useMemo(() => couples.filter((c) => {
    if (range === 'This week' && daysAgo(c.enquired_at) > 7) return false;
    if (show === 'Not booked' && c.slot_at) return false;
    if (!['All', 'Not booked'].includes(show) && c.host_id !== show) return false;
    if (q.trim()) {
      const s = q.toLowerCase();
      if (![c.names, c.email, c.phone].some((v) => (v || '').toLowerCase().includes(s))) return false;
    }
    return true;
  }), [couples, show, range, q]);

  const counts = useMemo(() => ({
    none: couples.filter((c) => coupleStatus(c) === 'none').length,
    booked: couples.filter((c) => coupleStatus(c) === 'booked').length,
    entered: couples.filter((c) => coupleStatus(c) === 'entered').length,
  }), [couples]);

  const setHost = async (c, hostId) => {
    const { error } = await supabase.rpc('wyw_set_host', { p_couple: c.id, p_host: hostId || null });
    if (error) notify.notifyError(errorText(error)); else load();
  };

  const open = couples.find((c) => c.id === openId);
  const booking = couples.find((c) => c.id === bookId);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto text-[#2B2B2B]">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-3xl font-bold" style={{ color: WYW_GREEN, fontFamily: 'Georgia, serif' }}>Win Your Wedding</h1>
        <div className="flex gap-2">
          <Chip on={tab === 'leads'} onClick={() => setTab('leads')}>Couples</Chip>
          <Chip on={tab === 'draw'} onClick={() => setTab('draw')}>Final Night</Chip>
          {tab === 'leads' && <button onClick={() => setAdding(true)} className="h-10 px-4 rounded-full text-white font-bold" style={{ background: WYW_GREEN }}>+ Add couple</button>}
        </div>
      </div>

      {loading && <p className="text-[#5E5E5E]">Loading…</p>}

      {!loading && tab === 'leads' && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[['none', 'Not booked yet'], ['booked', 'Booked a show-around'], ['entered', 'Fully entered']].map(([k, l]) => (
              <div key={k} className="rounded-xl px-5 py-4" style={k === 'booked' ? { background: '#fff' } : STATUS_STYLE[k]}>
                <div className="text-[15px]">{l}</div><div className="text-3xl font-bold">{counts[k]}</div>
              </div>
            ))}
          </div>
          {couples.length > 0 && (
            <p className="text-sm text-[#5E5E5E] -mt-2 mb-4">
              Where they came from: {SOURCE_ORDER.filter((k) => couples.some((c) => c.source === k))
                .map((k) => `${SOURCE_LABEL[k]} ${couples.filter((c) => c.source === k).length}`).join(' · ')}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex flex-wrap gap-2">
              {['All', 'Not booked', ...hosts.map((h) => h.id)].map((k) => (
                <Chip key={k} on={show === k} warm={k === 'Not booked'} onClick={() => setShow(k)}>{hosts.find((h) => h.id === k)?.name || k}</Chip>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[15px] text-[#5E5E5E]">Enquired</span>
              {['This week', 'Any time'].map((k) => <Chip key={k} on={range === k} onClick={() => setRange(k)}>{k}</Chip>)}
            </div>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" className="w-full md:w-80 h-11 px-3 mb-3 rounded-lg border border-[#D6D2C8] text-base" />

          <div className="bg-white rounded-xl overflow-x-auto">
            <table className="w-full text-base min-w-[860px]">
              <thead>
                <tr className="text-left text-[13px] font-bold tracking-wide text-[#5E5E5E] border-b border-[#ECE8E0]">
                  <th className="px-5 py-3">COUPLE</th><th className="px-3">ENQUIRED</th><th className="px-3">GUESTS</th>
                  <th className="px-3">SHOW-AROUND</th><th className="px-3">HOST</th><th className="px-3">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const st = coupleStatus(c);
                  return (
                    <tr key={c.id} className="border-b border-[#F1EEE8]">
                      <td className="px-5 py-3">
                        <button onClick={() => setOpenId(c.id)} className="text-left">
                          <div className="font-bold" style={{ color: WYW_GREEN }}>{c.names}</div>
                          <div className="text-sm text-[#5E5E5E]">{c.phone || c.email}</div>
                        </button>
                      </td>
                      <td className="px-3 text-[#5E5E5E]">{fmtAgo(c.enquired_at)}</td>
                      <td className="px-3">{c.guests || '—'}</td>
                      <td className="px-3">
                        {c.slot_at
                          ? <button onClick={() => setBookId(c.id)} className="font-bold text-left" style={{ color: WYW_GREEN }}>{fmtSlot(c.slot_at)}</button>
                          : <button onClick={() => setBookId(c.id)} className="h-9 px-4 rounded-lg text-white font-bold text-[15px]" style={{ background: WYW_GREEN }}>Book a slot</button>}
                      </td>
                      <td className="px-3">
                        <select value={c.host_id || ''} onChange={(e) => setHost(c, e.target.value)}
                          className="h-9 pl-2 pr-7 rounded-lg border text-[15px] font-bold"
                          style={c.host_id ? { borderColor: '#D6D2C8' } : { borderColor: '#E2B26B', background: '#FFF4E5', color: '#8A4B00' }}>
                          <option value="">Assign</option>
                          {hosts.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </select>
                      </td>
                      <td className="px-3"><span className="text-sm font-bold px-3 py-1.5 rounded-full whitespace-nowrap" style={STATUS_STYLE[st]}>{STATUS_LABEL[st]}</span></td>
                    </tr>
                  );
                })}
                {!rows.length && <tr><td colSpan={6} className="px-5 py-6 text-[#5E5E5E]">No couples here.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[#5E5E5E] mt-3">Booking a slot sets the host automatically (Niall and Jacqueline first, then Ronan and Simone). Change it with the host dropdown.</p>

          <details className="mt-6 bg-white rounded-xl p-4">
            <summary className="font-bold cursor-pointer" style={{ color: WYW_GREEN }}>Links</summary>
            <div className="mt-3 space-y-2 text-[15px]">
              <div className="flex flex-wrap items-center gap-3"><span className="w-40">Entry form</span><CopyButton text={enterLink()} label="Copy" /></div>
              {hosts.map((h) => (
                <div key={h.id} className="flex flex-wrap items-center gap-3"><span className="w-40">{h.name}'s show-arounds</span><CopyButton text={tonightLink(h.token)} label="Copy" />
                  {!h.email && <span className="text-[#8A4B00]">No email yet: summaries go to Ronan</span>}</div>
              ))}
            </div>
          </details>
        </>
      )}

      {!loading && tab === 'draw' && <DrawNight couples={couples} settings={settings} draw={draw} reload={load} />}

      {open && <CoupleRecord couple={open} hosts={hosts} onClose={() => setOpenId(null)} onChanged={load} onBook={() => setBookId(open.id)} />}
      {booking && <SlotPicker couple={booking} evenings={evenings} onClose={() => setBookId(null)} onBooked={() => { setBookId(null); load(); }} />}
      {adding && <AddCouple onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {ticket && <TicketCheckIn token={ticket} onDone={() => { setTicket(null); load(); }} />}
    </div>
  );
}
